import { useNavigate } from 'react-router-dom';
import { useCallback, useMemo, useState, useRef } from 'react';
import { ToastContainer } from 'react-toastify';
import Template from './components/common/Template.js';
import EvaluationList from './components/evaluationList/evaluationList.js';
import MarkView from './components/evaluationList/markView.js';
import StudentList from './components/studentlist/studentList.js';
import Dashboard from './components/dashboard/dashboard.js';
import '../../Assets/css/vendors/bootstrap.css';

export const parseHashSegments = (hash) => {
    const hashContent = hash.startsWith('#') ? hash.slice(1) : hash;
    const parts = hashContent.split('/').filter(p => p !== '' && p !== 'evaluation-list' && p !== 'student-list' && p !== 'dashboard');

    let studentId = null;
    const filterParts = [];

    parts.forEach(part => {
        if (part.startsWith('filter=')) {
            filterParts.push(part);
        } else if (!isNaN(part) && part !== '') {
            studentId = part;
        }
    });

    return { studentId };
};

function Base() {
    const navigate = useNavigate();
    const [EvaluationListRefreshKey, setEvaluationListRefreshKey] = useState(0);
    const refreshKeyRef = useRef(EvaluationListRefreshKey);

    const pendingRefreshRef = useRef(false);

    const triggerEvaluationListRefresh = useCallback(() => {
        setEvaluationListRefreshKey(prev => {
            const next = prev + 1;
            refreshKeyRef.current = next;
            return next;
        });
    }, []);

    const handleViewMarks = useCallback((studentId) => {
        const currentHash = window.location.hash;
        const hashParts = currentHash.replace('#', '').split('/');
        const filterPart = hashParts.find(p => p.startsWith('filter='));
        const pagePart = hashParts.find(p => p.startsWith('page='));

        let newHash = `/is/v/#evaluation-list/${studentId}`;
        if (filterPart) newHash += `/${filterPart}`;
        if (pagePart) newHash += `/${pagePart}`;

        const queryString = window.location.search;
        navigate(`${newHash}${queryString}`);
    }, [navigate]);

    const handleBackToList = useCallback(() => {
        const currentHash = window.location.hash;
        const hashParts = currentHash.replace('#', '').split('/');
        const filterPart = hashParts.find(p => p.startsWith('filter='));

        let newHash = '/is/v/#evaluation-list';
        if (filterPart) newHash += `/${filterPart}`;

        const queryString = window.location.search;
        navigate(`${newHash}${queryString}`);

        if (pendingRefreshRef.current) {
            pendingRefreshRef.current = false;
            triggerEvaluationListRefresh();
        }
    }, [navigate, triggerEvaluationListRefresh]);

    const handleSubmitSuccess = useCallback(() => {
        pendingRefreshRef.current = true;
    }, []);

    const viewComponents = useMemo(() => ({
        'dashboard': ({ hashParams }) => {
            return (
                <Dashboard />
            );
        },
        'student-list': ({ hashParams }) => {
            return (
                <StudentList />
            );
        },
        'evaluation-list': ({ hashParams }) => {
            const { studentId } = parseHashSegments(window.location.hash);

            return (
                <>
                    <div style={{ display: studentId ? 'none' : 'block' }}>
                        <EvaluationList
                            onViewMarks={handleViewMarks}
                            refreshKey={refreshKeyRef.current}
                            enabled={!studentId}
                        />
                    </div>

                    {studentId && (
                        <MarkView
                            studentId={studentId}
                            onBack={handleBackToList}
                            onSubmitSuccess={handleSubmitSuccess}
                        />
                    )}
                </>
            );
        },
    }), [handleViewMarks, handleBackToList, handleSubmitSuccess]);

    return (
        <>
            <ToastContainer />
            <Template viewComponents={viewComponents}>
                <style>{`
                    .table td, .table th {
                        white-space: normal;
                        overflow-wrap: break-word;
                    }

                    @media only screen and (max-width: 760px) {
                        .card .card-body {
                            padding: 0.5rem;
                        }
                        table, tbody, thead, th, td, tr {
                            display: block;
                            float: left;
                            width: 100%;
                            clear: both;
                        }
                        .table-sm> :not(caption)>*>* {
                            padding: 0.25rem 0.35rem 0.25rem 0rem;
                        }
                        .table> :not(caption)>*>* {
                            padding: 0.5rem 0.5rem 0.5rem 0rem;
                        }
                        thead tr {
                            position: absolute;
                            top: -9999px;
                            left: -9999px;
                        }
                        tr {
                            border: 1px solid #ccc;
                            margin-bottom: 10px;
                            padding: 10px;
                        }
                        td, th {
                            border: none;
                            border-bottom: 1px solid #eee;
                            position: relative;
                            display: flex;
                            justify-content: space-between;
                            box-sizing: border-box;
                            padding: 10px;
                        }
                        td::before {
                            content: attr(data-label);
                            font-weight: bold;
                            color: #6c757d;
                            background-color: rgb(255, 255, 255);
                            z-index: 10;
                            padding: 0px 15px 0px 15px;
                        }
                        .dark-version td::before {
                            background-color: #1A2035;
                        }
                    }
                    .togl-sec-2btn, .togl-sec {
                        cursor: default !important;
                    }
                    .switch-loading .slider {
                        background: linear-gradient(90deg, #cbd5e0 0%, #a0aec0 50%, #cbd5e0 100%) !important;
                        background-size: 200% 100%;
                        animation: shimmer 1.5s ease-in-out infinite;
                        opacity: 0.7;
                        cursor: not-allowed;
                    }
                    .switch-loading .slider:before {
                        opacity: 0.5;
                    }
                    @keyframes shimmer {
                        0% { background-position: 200% 0; }
                        100% { background-position: -200% 0; }
                    }
                    .switch-loading {
                        animation: pulse 2s ease-in-out infinite;
                    }
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.8; }
                    }
                `}</style>
            </Template>
        </>
    );
}

export default Base;